import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Pagination,
  Stack,
  Typography,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { PropagateLoader } from "react-spinners";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import config from "../../Config";

const COLORS = {
  cream: "#F7F4EF",
  softCream: "#FBF9F6",
  white: "#FFFFFF",
  black: "#171717",
  mutedBlack: "#3A3A3A",
  grey: "#77736D",
  lightGrey: "#A29D95",
  border: "#DDD7CE",
  gold: "#977743",
  goldLight: "#C4A36A",
};

const ITEMS_PER_PAGE = 6;

const SidebarItem = ({ icon, label, active, onClick }) => (
  <ListItem
    component="button"
    onClick={onClick}
    sx={{
      width: "100%",
      border: 0,
      outline: "none",
      textAlign: "left",
      background: active ? COLORS.black : "transparent",
      color: active ? COLORS.white : COLORS.mutedBlack,
      borderRadius: 0,
      px: 2,
      py: 1.6,
      mb: 0.6,
      cursor: "pointer",
      transition: "all 0.25s ease",
      "&:hover": {
        background: active ? COLORS.black : COLORS.cream,
        color: active ? COLORS.white : COLORS.black,
        transform: "translateX(3px)",
      },
      "& .MuiListItemIcon-root": {
        minWidth: 40,
        color: active ? COLORS.goldLight : COLORS.grey,
        transition: "color 0.25s ease",
      },
    }}
  >
    <ListItemIcon>{icon}</ListItemIcon>

    <ListItemText
      primary={label}
      primaryTypographyProps={{
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        letterSpacing: "0.02em",
      }}
    />

    <ArrowForwardIosRoundedIcon
      sx={{
        fontSize: 12,
        opacity: active ? 0.9 : 0,
        transform: active ? "translateX(0)" : "translateX(-5px)",
        transition: "all 0.25s ease",
      }}
    />
  </ListItem>
);

export default function BookingRequest() {
  const navigate = useNavigate();

  const [bookings, setBookings] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const openAppRedirect = () => {
    const userId = sessionStorage.getItem("userId");

    if (!userId) {
      navigate("/Signin");
      return;
    }

    const appPackage = "com.ytctechventures.hindustanwardrobe";

    const intentUrl = `intent://ownerProfile?ownerId=${userId}#Intent;scheme=https;package=${appPackage};end`;

    const playStoreUrl =
      "https://play.google.com/store/apps/details?id=com.ytctechventures.hindustanwardrobe";

    window.location.href = intentUrl;

    setTimeout(() => {
      window.location.href = playStoreUrl;
    }, 1500);
  };

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const userId = sessionStorage.getItem("userId");

      if (!userId) {
        navigate("/Signin");
        return;
      }

      const response = await axios.get(
        `${config.apiBaseUrl}/${config.bookingRequest}`,
        {
          headers: {
            "user-id": userId,
          },
        },
      );

      setBookings(response.data?.BookingRequests || []);
    } catch (err) {
      if (err.response?.status === 404) {
        setBookings([]);
      } else {
        setError(
          err.response?.data?.message ||
            err.message ||
            "Unable to load booking requests.",
        );
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const totalPages = Math.max(1, Math.ceil(bookings.length / ITEMS_PER_PAGE));

  const visibleBookings = useMemo(() => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;

    return bookings.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [bookings, page]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handlePageChange = (_, value) => {
    setPage(value);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const getStatusStyle = (status) => {
    const value = String(status || "").toLowerCase();

    if (
      value.includes("confirm") ||
      value.includes("approve") ||
      value.includes("accept")
    ) {
      return {
        backgroundColor: "#EEF4ED",
        color: "#42613D",
      };
    }

    if (
      value.includes("cancel") ||
      value.includes("reject") ||
      value.includes("decline")
    ) {
      return {
        backgroundColor: "#F8EDED",
        color: "#8B4A4A",
      };
    }

    return {
      backgroundColor: COLORS.cream,
      color: COLORS.gold,
    };
  };

  return (
    <>
      <Navbar />

      <Box
        sx={{
          backgroundColor: COLORS.cream,
          minHeight: "calc(100vh - 100px)",
          py: { xs: 4, sm: 5, md: 7 },
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            maxWidth: 1400,
            px: { xs: 2, sm: 3, md: 5 },
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "250px minmax(0, 1fr)",
              },
              gap: { xs: 0, md: 5 },
              alignItems: "start",
            }}
          >
            <Box
              sx={{
                display: {
                  xs: "none",
                  sm: "none",
                  md: "block",
                },
              }}
            >
              <Box
                sx={{
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: COLORS.white,
                  borderRadius: 0,
                  overflow: "hidden",
                  position: "sticky",
                  top: 100,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: COLORS.black,
                    color: COLORS.white,
                    p: 2.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 10,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      color: COLORS.goldLight,
                      mb: 0.7,
                    }}
                  >
                    Account
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: "Georgia, serif",
                      fontSize: 24,
                    }}
                  >
                    My Wardrobe
                  </Typography>
                </Box>

                <List sx={{ p: 1.2 }}>
                  <SidebarItem
                    icon={<PersonIcon />}
                    label="Profile"
                    onClick={() => navigate("/profile")}
                  />

                  <SidebarItem
                    icon={<ShoppingBagIcon />}
                    label="Booking Request"
                    active
                    onClick={openAppRedirect}
                  />

                  <SidebarItem
                    icon={<ShoppingCartIcon />}
                    label="My Orders"
                    onClick={() => navigate("/orders")}
                  />

                  <SidebarItem
                    icon={<FavoriteIcon />}
                    label="Wishlist"
                    onClick={() => navigate("/wishlist")}
                  />

                  <SidebarItem
                    icon={<SupportAgentIcon />}
                    label="Help & Support"
                    onClick={() => navigate("/contact-us")}
                  />
                </List>
              </Box>
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  mb: { xs: 4, md: 5 },
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                  gap: 3,
                  flexWrap: "wrap",
                }}
              >
                <Box>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 1.5 }}
                  >
                    <CalendarMonthOutlinedIcon
                      sx={{
                        fontSize: 18,
                        color: COLORS.gold,
                      }}
                    />

                    <Typography
                      sx={{
                        fontSize: 10,
                        letterSpacing: "0.19em",
                        textTransform: "uppercase",
                        color: COLORS.gold,
                        fontWeight: 700,
                      }}
                    >
                      Your Requests
                    </Typography>
                  </Stack>

                  <Typography
                    component="h1"
                    sx={{
                      fontFamily: "Georgia, serif",
                      fontSize: {
                        xs: 34,
                        sm: 44,
                        md: 52,
                      },
                      fontWeight: 400,
                      lineHeight: 1.05,
                      color: COLORS.black,
                      mb: 1.5,
                    }}
                  >
                    Booking Requests
                  </Typography>

                  <Typography
                    sx={{
                      color: COLORS.grey,
                      fontSize: 14,
                      lineHeight: 1.8,
                      maxWidth: 620,
                    }}
                  >
                    Keep track of the pieces you've requested and follow every
                    booking from request to confirmation.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    minWidth: { xs: "100%", sm: 145 },
                    border: `1px solid ${COLORS.border}`,
                    backgroundColor: COLORS.softCream,
                    px: 2.5,
                    py: 1.8,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 9,
                      letterSpacing: "0.17em",
                      textTransform: "uppercase",
                      color: COLORS.lightGrey,
                      mb: 0.5,
                    }}
                  >
                    Total Requests
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: "Georgia, serif",
                      fontSize: 27,
                      color: COLORS.black,
                    }}
                  >
                    {bookings.length}
                  </Typography>
                </Box>
              </Box>

              {loading ? (
                <Box
                  sx={{
                    minHeight: 450,
                    border: `1px solid ${COLORS.border}`,
                    backgroundColor: COLORS.white,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <PropagateLoader color={COLORS.gold} size={8} />

                  <Typography
                    sx={{
                      mt: 1,
                      fontSize: 10,
                      letterSpacing: "0.17em",
                      textTransform: "uppercase",
                      color: COLORS.grey,
                    }}
                  >
                    Loading your requests
                  </Typography>
                </Box>
              ) : error ? (
                <Box
                  sx={{
                    minHeight: 400,
                    border: `1px solid ${COLORS.border}`,
                    backgroundColor: COLORS.white,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    px: 3,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: "Georgia, serif",
                        fontSize: 30,
                        color: COLORS.black,
                        mb: 1,
                      }}
                    >
                      Something went wrong
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 14,
                        color: COLORS.grey,
                        lineHeight: 1.7,
                        mb: 3,
                      }}
                    >
                      {error}
                    </Typography>

                    <Box
                      component="button"
                      onClick={fetchBookings}
                      sx={{
                        border: 0,
                        backgroundColor: COLORS.black,
                        color: COLORS.white,
                        px: 3,
                        py: 1.5,
                        fontSize: 10,
                        letterSpacing: "0.15em",
                        textTransform: "uppercase",
                        fontWeight: 700,
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: COLORS.gold,
                        },
                      }}
                    >
                      Try Again
                    </Box>
                  </Box>
                </Box>
              ) : bookings.length === 0 ? (
                <Box
                  sx={{
                    minHeight: 430,
                    border: `1px solid ${COLORS.border}`,
                    backgroundColor: COLORS.white,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    px: 3,
                  }}
                >
                  <Box>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        mx: "auto",
                        mb: 2.5,
                        border: `1px solid ${COLORS.border}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <ReceiptLongOutlinedIcon
                        sx={{
                          fontSize: 27,
                          color: COLORS.gold,
                        }}
                      />
                    </Box>

                    <Typography
                      sx={{
                        fontFamily: "Georgia, serif",
                        fontSize: 30,
                        color: COLORS.black,
                        mb: 1,
                      }}
                    >
                      No booking requests
                    </Typography>

                    <Typography
                      sx={{
                        maxWidth: 440,
                        mx: "auto",
                        fontSize: 14,
                        color: COLORS.grey,
                        lineHeight: 1.8,
                        mb: 3,
                      }}
                    >
                      Your requested pieces will appear here once you make a
                      booking request.
                    </Typography>

                    <Box
                      component="button"
                      onClick={() => navigate("/AllProduct")}
                      sx={{
                        border: 0,
                        backgroundColor: COLORS.black,
                        color: COLORS.white,
                        px: 3,
                        py: 1.5,
                        fontSize: 10,
                        letterSpacing: "0.15em",
                        textTransform: "uppercase",
                        fontWeight: 700,
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: COLORS.gold,
                        },
                      }}
                    >
                      Explore Collection
                    </Box>
                  </Box>
                </Box>
              ) : (
                <>
                  <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
                    {visibleBookings.map((bookingItem, index) => {
                      const booking = bookingItem?.bookingData || {};

                      const product = booking.productData?.product || {};

                      const image =
                        booking.productData?.productImage?.[0] ||
                        "/placeholder.svg?height=400&width=300";

                      return (
                        <Grid
                          item
                          xs={12}
                          md={6}
                          key={booking.bookingId || bookingItem?._id || index}
                        >
                          <Card
                            elevation={0}
                            onClick={() =>
                              navigate(`/BookingDetails/${booking.bookingId}`)
                            }
                            sx={{
                              height: "100%",
                              borderRadius: 0,
                              border: `1px solid ${COLORS.border}`,
                              backgroundColor: COLORS.white,
                              overflow: "hidden",
                              cursor: "pointer",
                              transition:
                                "transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
                              "&:hover": {
                                transform: "translateY(-5px)",
                                borderColor: COLORS.goldLight,
                                boxShadow: "0 18px 45px rgba(23,23,23,0.09)",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                  xs: "1fr",
                                  sm: "1.1fr 0.9fr",
                                },
                                minHeight: {
                                  xs: "auto",
                                  sm: 245,
                                },
                              }}
                            >
                              <CardContent
                                sx={{
                                  p: {
                                    xs: 2.2,
                                    sm: 2.8,
                                  },
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                    gap: 2,
                                    mb: 2,
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      sx={{
                                        fontSize: 9,
                                        letterSpacing: "0.16em",
                                        textTransform: "uppercase",
                                        color: COLORS.gold,
                                        fontWeight: 700,
                                        mb: 0.7,
                                      }}
                                    >
                                      Booking Request
                                    </Typography>

                                    <Typography
                                      sx={{
                                        fontFamily: "Georgia, serif",
                                        fontSize: 20,
                                        lineHeight: 1.25,
                                        color: COLORS.black,
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {product.title || "Fashion Piece"}
                                    </Typography>
                                  </Box>

                                  <Chip
                                    label={booking.bookingStatus || "Pending"}
                                    size="small"
                                    sx={{
                                      height: 25,
                                      borderRadius: 0,
                                      flexShrink: 0,
                                      fontSize: 9,
                                      letterSpacing: "0.08em",
                                      textTransform: "uppercase",
                                      fontWeight: 700,
                                      ...getStatusStyle(booking.bookingStatus),
                                    }}
                                  />
                                </Box>

                                <Divider
                                  sx={{
                                    borderColor: COLORS.border,
                                    mb: 2,
                                  }}
                                />

                                <Stack spacing={1.2} sx={{ mb: 2 }}>
                                  <Box>
                                    <Typography
                                      sx={{
                                        fontSize: 9,
                                        color: COLORS.lightGrey,
                                        letterSpacing: "0.1em",
                                        textTransform: "uppercase",
                                        mb: 0.3,
                                      }}
                                    >
                                      Booking ID
                                    </Typography>

                                    <Typography
                                      sx={{
                                        fontSize: 12,
                                        color: COLORS.mutedBlack,
                                        fontWeight: 600,
                                        wordBreak: "break-all",
                                      }}
                                    >
                                      {booking.bookingId || "—"}
                                    </Typography>
                                  </Box>

                                  <Stack direction="row" spacing={3}>
                                    <Box>
                                      <Typography
                                        sx={{
                                          fontSize: 9,
                                          color: COLORS.lightGrey,
                                          letterSpacing: "0.1em",
                                          textTransform: "uppercase",
                                          mb: 0.3,
                                        }}
                                      >
                                        Rent
                                      </Typography>

                                      <Typography
                                        sx={{
                                          fontSize: 14,
                                          color: COLORS.black,
                                          fontWeight: 700,
                                        }}
                                      >
                                        ₹{product.rentAmount || 0}
                                        <Typography
                                          component="span"
                                          sx={{
                                            fontSize: 10,
                                            color: COLORS.grey,
                                            fontWeight: 400,
                                            ml: 0.4,
                                          }}
                                        >
                                          / day
                                        </Typography>
                                      </Typography>
                                    </Box>

                                    {product.size && (
                                      <Box>
                                        <Typography
                                          sx={{
                                            fontSize: 9,
                                            color: COLORS.lightGrey,
                                            letterSpacing: "0.1em",
                                            textTransform: "uppercase",
                                            mb: 0.3,
                                          }}
                                        >
                                          Size
                                        </Typography>

                                        <Typography
                                          sx={{
                                            fontSize: 13,
                                            color: COLORS.black,
                                            fontWeight: 600,
                                          }}
                                        >
                                          {product.size}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Stack>
                                </Stack>

                                <Box
                                  sx={{
                                    mt: "auto",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    pt: 1,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: 9,
                                      letterSpacing: "0.14em",
                                      textTransform: "uppercase",
                                      color: COLORS.gold,
                                      fontWeight: 700,
                                    }}
                                  >
                                    View Request
                                  </Typography>

                                  <Typography
                                    sx={{
                                      fontSize: 17,
                                      color: COLORS.black,
                                    }}
                                  >
                                    →
                                  </Typography>
                                </Box>
                              </CardContent>

                              <Box
                                sx={{
                                  position: "relative",
                                  minHeight: {
                                    xs: 250,
                                    sm: "100%",
                                  },
                                  backgroundColor: COLORS.cream,
                                  overflow: "hidden",
                                }}
                              >
                                <CardMedia
                                  component="img"
                                  image={image}
                                  alt={product.title || "Fashion item"}
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    minHeight: {
                                      xs: 250,
                                      sm: 245,
                                    },
                                    objectFit: "cover",
                                    transition: "transform 0.5s ease",
                                    "&:hover": {
                                      transform: "scale(1.04)",
                                    },
                                  }}
                                />

                                {product.isSecurity && (
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      bottom: 12,
                                      left: 12,
                                      backgroundColor: COLORS.white,
                                      border: `1px solid ${COLORS.border}`,
                                      px: 1.2,
                                      py: 0.7,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: 8,
                                        letterSpacing: "0.1em",
                                        textTransform: "uppercase",
                                        color: COLORS.gold,
                                        fontWeight: 700,
                                      }}
                                    >
                                      Security Required
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>

                  {totalPages > 1 && (
                    <Box
                      sx={{
                        mt: { xs: 5, md: 7 },
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handlePageChange}
                        shape="rounded"
                        sx={{
                          "& .MuiPaginationItem-root": {
                            borderRadius: 0,
                            color: COLORS.mutedBlack,
                            fontSize: 12,
                          },
                          "& .Mui-selected": {
                            backgroundColor: COLORS.black,
                            color: COLORS.white,
                          },
                          "& .Mui-selected:hover": {
                            backgroundColor: COLORS.gold,
                          },
                        }}
                      />
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      <Footer />
    </>
  );
}
